// src/pushNotification.js
import { initializeFirebaseMessaging, buildFirebaseConfig } from './firebase';
import { fetchRegisterDeviceToken } from './api/api.app';

const SW_PATH = '/firebase-messaging-sw.js';

export const isPushSupported = () =>
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const isPushConfigured = (webParams) =>
    Boolean(webParams && webParams.firebase_project_id && webParams.firebase_v_api_key);

// Le service worker est statique et commun à tous les domaines : sa configuration Firebase
// lui arrive par la query string de son URL d'enregistrement.
const buildServiceWorkerUrl = (webParams) => {
    const config = buildFirebaseConfig(webParams);
    const params = new URLSearchParams();

    Object.keys(config).forEach((key) => {
        if (config[key]) {
            params.set(key, config[key]);
        }
    });

    return SW_PATH + '?' + params.toString();
};

const scriptUrlOf = (registration) => {
    const worker = registration.active || registration.waiting || registration.installing;
    return worker ? worker.scriptURL : null;
};

// Un service worker est identifié par son URL complète, query string comprise : si la config
// Firebase du site change, l'ancienne registration survit en parallèle et doit être retirée.
const registerServiceWorker = async (swUrl) => {
    const expectedUrl = new URL(swUrl, window.location.origin).href;
    const registrations = await navigator.serviceWorker.getRegistrations();

    await Promise.all(
        registrations
            .filter((registration) => {
                const scriptUrl = scriptUrlOf(registration);
                return scriptUrl && scriptUrl.indexOf(SW_PATH) !== -1 && scriptUrl !== expectedUrl;
            })
            .map((registration) => registration.unregister())
    );

    return navigator.serviceWorker.register(swUrl);
};

const registerDeviceToken = async (messaging, registration, webParams) => {
    const token = await messaging.getToken({
        vapidKey: webParams.firebase_v_api_key,
        serviceWorkerRegistration: registration,
    });

    if (!token) {
        console.warn('Aucun token FCM disponible');
        return null;
    }

    await fetchRegisterDeviceToken(token);

    return token;
};

// Les messages reçus application au premier plan ne passent pas par le service worker :
// c'est à l'application de les afficher.
const displayForegroundMessages = (messaging, registration) => {
    messaging.onMessage((payload) => {
        const content = payload.notification || payload.data;

        if (!content || !content.title || Notification.permission !== 'granted') {
            return;
        }

        registration.showNotification(content.title, {
            body: content.body,
            icon: content.icon || content.image,
            data: { url: (payload.data && (payload.data.url || payload.data.click_action)) || null },
        });
    });
};

/**
 * Prépare le push sans jamais demander de permission : enregistre le service worker et, si
 * l'utilisateur a déjà accepté, rafraîchit son token. Appelable à l'init de l'application.
 */
export const initializePushNotifications = async (webParams) => {
    if (!isPushSupported() || !isPushConfigured(webParams)) {
        return null;
    }

    try {
        const messaging = await initializeFirebaseMessaging(webParams);

        if (!messaging) {
            return null;
        }

        const registration = await registerServiceWorker(buildServiceWorkerUrl(webParams));

        displayForegroundMessages(messaging, registration);

        if (Notification.permission !== 'granted') {
            return null;
        }

        return await registerDeviceToken(messaging, registration, webParams);
    } catch (error) {
        console.warn('Initialisation du push impossible', error);
        return null;
    }
};

/**
 * Demande la permission puis enregistre le token. Doit être appelée depuis un geste
 * utilisateur : Safari l'exige, et les autres navigateurs pénalisent les demandes spontanées.
 */
export const requestPushPermission = async (webParams) => {
    if (!isPushSupported() || !isPushConfigured(webParams)) {
        return null;
    }

    try {
        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
            return null;
        }

        const messaging = await initializeFirebaseMessaging(webParams);

        if (!messaging) {
            return null;
        }

        const registration = await registerServiceWorker(buildServiceWorkerUrl(webParams));

        displayForegroundMessages(messaging, registration);

        return await registerDeviceToken(messaging, registration, webParams);
    } catch (error) {
        console.warn('Activation du push impossible', error);
        return null;
    }
};